import { pgTable, uuid, text, integer, timestamp, jsonb, index, boolean, uniqueIndex } from "drizzle-orm/pg-core";

/** Staff-only annotation on a conversation message. NEVER expose to contacts. */
export interface InternalNote {
  authorId: string;
  content: string;
  createdAt: string; // ISO timestamp
}

export const conversations = pgTable("conversations", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  personId: uuid("person_id").notNull(),
  channel: text("channel").notNull(),  // whatsapp, instagram_dm, facebook_messenger, sms, email
  externalThreadId: text("external_thread_id"),  // platform-specific thread ID
  status: text("status").default("open").notNull(),  // open, closed, snoozed
  assignedTo: text("assigned_to"),  // userId
  /** INB-E: SLA priority lane — drives escalation budget. Constraint enforces enum. */
  priority: text("priority").default("normal").notNull(),  // low | normal | high | urgent
  /** INB-D: first time the conversation was assigned. Write-once for time-to-claim. */
  firstAssignedAt: timestamp("first_assigned_at", { withTimezone: true }),
  /** INB-D: first agent reply timestamp. Write-once for time-to-first-response. */
  firstResponseAt: timestamp("first_response_at", { withTimezone: true }),
  /** INB-E: write-once watermark — the SLA cron sets this when it fires
   * `conversation.escalated` so retries never double-emit. NULL means no
   * breach yet (or breach not yet detected). */
  escalatedAt: timestamp("escalated_at", { withTimezone: true }),
  lastMessageAt: timestamp("last_message_at", { withTimezone: true }),
  /** INB-F: latest @-mention written into this thread. NULL = never mentioned.
   * Drives the inbox @-mentions tab and orders pickup queues by latest
   * handoff without scanning the immutable event log. */
  lastMentionAt: timestamp("last_mention_at", { withTimezone: true }),
  messageCount: integer("message_count").default(0).notNull(),
  unreadCount: integer("unread_count").default(0).notNull(),
  metadata: jsonb("metadata").default({}).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_conversations_tenant_person").on(table.tenantId, table.personId),
  index("idx_conversations_tenant_status").on(table.tenantId, table.status),
  index("idx_conversations_tenant_assigned").on(table.tenantId, table.assignedTo),
  index("idx_conversations_last_message").on(table.tenantId, table.lastMessageAt),
]);

export const CONVERSATION_STATUSES = ["open", "closed", "snoozed"] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

/** INB-E: priority enum — order is significant for SLA budget lookup. */
export const CONVERSATION_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type ConversationPriority = (typeof CONVERSATION_PRIORITIES)[number];

export const conversationMessages = pgTable("conversation_messages", {
  id: uuid("id").primaryKey().defaultRandom(),
  conversationId: uuid("conversation_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  direction: text("direction").notNull(),  // inbound, outbound
  channel: text("channel").notNull(),
  body: text("body"),  // message content — PII, encrypted for tier_2+
  contentType: text("content_type").default("text").notNull(),  // text, image, video, document, template, location
  contentEncryptionKeyId: text("content_encryption_key_id"),  // for tier_2+ encryption
  externalMessageId: text("external_message_id"),
  senderType: text("sender_type").notNull(),  // contact, agent, system
  senderId: text("sender_id"),  // userId for agents, personId for contacts
  metadata: jsonb("metadata").default({}).notNull(),  // channel-specific data
  /** Internal staff annotations. NEVER include in contact-facing API responses. Serializer must strip for non-staff. */
  internalNotes: jsonb("internal_notes").$type<InternalNote[]>(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_conv_messages_conversation").on(table.conversationId),
  index("idx_conv_messages_tenant").on(table.tenantId),
  uniqueIndex("uq_conv_messages_external").on(table.tenantId, table.externalMessageId),
]);
